import { useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { BaseAlertModal } from "../../../../../components/modals/alert";
import { Layout } from "../../../../../containers/layout/dashboard";
import { EditSystemUserWrapper } from "./styled";
import { BaseButton } from "../../../../../components/button/styled";
import { BaseFieldSet } from "../../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../../components/formfields/label/styled";
import { BaseInput } from "../../../../../components/formfields/input/styled";
import { BaseSelect } from "../../../../../components/formfields/select/styled";
import { BaseOption } from "../../../../../components/formfields/option/styled";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import Cookies from "universal-cookie";
import { useNavigate, useParams } from "react-router-dom";
import { Context } from "../../../../../context";
import { retrieveAllRolesService } from "../../../../../utils/apis/usermanagement/role/retrieveAllRoles";
import { retrieveInstitutionsService } from "../../../../../utils/apis/institution/retrieveInstitutions";
import { retrieveUserByIdService } from "../../../../../utils/apis/usermanagement/user/retrieveUserById";
import { retrieveRoleByIdService } from "../../../../../utils/apis/usermanagement/role/retrieveRoleById";
import { updateUserByIdService } from "../../../../../utils/apis/usermanagement/user/updateUserById";
import { useQuery } from "@tanstack/react-query";
import uploadImageIcon from "../../../../../assets/uploadimageicon.svg";

export const EditSystemUser = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { id = "" } = useParams();
    const { setActiveNavItem } = useContext(Context);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isMFIAdminRole, setIsMFIAdminRole] = useState(false);
    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [formDetails, setFormDetails] = useState<Record<string, any>>({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        role: " ",
        institution: " ",
        avatar: ""
    });
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    useEffect(() => {
        setActiveNavItem("User Management");
    }, [setActiveNavItem]);

    const { data: roles } = useQuery({
        queryKey: ['roles', TOKEN],
        queryFn: async () => retrieveAllRolesService("", TOKEN),
        enabled: !!TOKEN,
    });

    const { data: institutions } = useQuery({
        queryKey: ['institutions', TOKEN],
        queryFn: async () => retrieveInstitutionsService(TOKEN),
        enabled: !!TOKEN,
    });

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const data = await retrieveUserByIdService(TOKEN, id);
                const lastestRoleId = data?.roles?.[data.roles.length - 1];
                if (!lastestRoleId) {
                    return {
                        ...data,
                        role: "",
                    };
                };
                const role = await retrieveRoleByIdService(lastestRoleId, TOKEN);
                if (role.title === "MFI Admin") {
                    setIsMFIAdminRole(true);
                };
                const transformedUser = {
                    ...data,
                    role: role?.id,
                };
                setFormDetails(transformedUser);
            } catch (err) {
                console.error("Failed to fetch user:", err);
            }
        }
        fetchUser();
    }, [TOKEN, id]);

    const handleNavigateToPrevious = () => {
        return navigate(-1);
    };

    const handleAlertModalPersist = () => {
        return setOpenAlertModal(true);
    };

    const handleNavigateToSystemUsers = () => {
        navigate("/usermanagement/systemusers");
        return setOpenAlertModal(false);
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target as HTMLInputElement | HTMLTextAreaElement | { name: string; value: string };
        if (!isMFIAdminRole && name === "institution") return;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
        if (name === "role") {
            if (!value.trim()) return setIsMFIAdminRole(false);
            const response = await retrieveRoleByIdService(value, TOKEN);
            if (response.title === "MFI Admin") {
                setIsMFIAdminRole(true);
            } else {
                setIsMFIAdminRole(false);
            }
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                if (e.target?.result) {
                    setImagePreview(e.target.result as string);
                    setFormDetails((prev) => ({
                        ...prev,
                        avatar: file
                    }))
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        const formData = new FormData();
        const data = {
            firstName: formDetails.firstName,
            lastName: formDetails.lastName,
            phone: formDetails.phone,
            email: formDetails.email,
            role: formDetails.role,
            institution: formDetails.institution,
            avatar: formDetails.avatar,
        };
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });
        try {
            const response = await updateUserByIdService(id, formData, TOKEN);
            if (response.status === "success") {
                setIsLoading(false);
                setOpenAlertModal(true);
            } else {
                setIsLoading(false);
                setError('Failed to edit user. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to edit user. ${error.message}`);
            console.error('Failed to edit user:', error);
        }
    };

    return (
        <Layout
            id="system-users"
        >
            <EditSystemUserWrapper>
                <BaseAlertModal
                    title="Success"
                    message="User successfully edited"
                    open={openAlertModal}
                    handleClose={handleAlertModalPersist}
                    callToAction="Continue"
                    handleCallToAction={handleNavigateToSystemUsers}
                />
                <Box
                    component={"div"}
                    className="title-box"
                >
                    <Typography
                        variant="h1"
                        fontFamily={"Inter"}
                        fontWeight={700}
                        fontSize={24}
                        lineHeight={"normal"}
                        color={"#373737"}
                        marginBlockEnd={"calc(var(--basic-margin)/4)"}
                    >
                        Edit System User
                    </Typography>
                    <BaseButton
                        variant="outlined"
                        border="1px solid #D7D7D7"
                        colour="#373737"
                        bgcolor="#FFFFFF"
                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                        fontSize={12}
                        fontWeight={800}
                        startIcon={<ArrowLeftIcon />}
                        onClick={handleNavigateToPrevious}
                    >
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            Back
                        </Typography>
                    </BaseButton>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    First Name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="firstName"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter first name"
                                    value={formDetails.firstName}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Last Name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="lastName"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter last name"
                                    value={formDetails.lastName}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Email address
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="email"
                                    name="email"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="username@gmail.com"
                                    value={formDetails.email}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Phone number
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="phone"
                                    name="phone"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter phone number"
                                    value={formDetails.phone}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Picture
                                </BaseLabel>
                                <Stack
                                    overflow={"hidden"}
                                    gap={"calc(var(--flex-gap)/2)"}
                                    direction={{ mobile: "row" }}
                                    alignItems={{ mobile: "center" }}
                                    marginBlockStart={{ mobile: "calc(var(--basic-margin)/3)", laptop: 0 }}
                                >
                                    {imagePreview ? (
                                        <img
                                            src={imagePreview}
                                            alt="admin-avatar"
                                            className="admin-avatar"
                                        />
                                    ) : (
                                        <img
                                            src={formDetails?.avatar || uploadImageIcon}
                                            alt="admin-avatar"
                                            className="admin-avatar"
                                        />
                                    )}
                                    <BaseInput
                                        as={"input"}
                                        type="file"
                                        name="avatar"
                                        colour="#666666"
                                        bgcolor="#FFFFFF"
                                        fontWeight={400}
                                        sx={{ width: "100%", boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", flex: 1 }}
                                        inputProps={{ accept: "image/jpeg, image/png" }}
                                        onChange={handleFileChange}
                                    />
                                </Stack>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Role
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="role"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.role}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select role
                                    </BaseOption>
                                    {roles?.map((role: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={role.id}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {role.title}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        {isMFIAdminRole && (
                            <Grid2
                                size={{ mobile: 12, laptop: 6 }}
                            >
                                <BaseFieldSet>
                                    <BaseLabel
                                        colour="#333333"
                                        fontWeight={500}
                                    >
                                        Institution
                                    </BaseLabel>
                                    <BaseSelect
                                        required
                                        name="institution"
                                        colour="#666666"
                                        bgcolor="#FFFFFF"
                                        fontWeight={400}
                                        readOnly={isMFIAdminRole ? false : true}
                                        // IconComponent={<KeyboardArrowDown />}
                                        value={formDetails.institution}
                                        onChange={(e) => handleChange(e)}
                                    >
                                        <BaseOption
                                            value={" "}
                                            colour="#666666"
                                            fontWeight={400}
                                        >
                                            Select institution
                                        </BaseOption>
                                        {institutions?.map((institution: Record<any, any>, index: string) => {
                                            return (
                                                <BaseOption
                                                    key={index}
                                                    value={institution.id}
                                                    colour="#666666"
                                                    fontWeight={400}
                                                >
                                                    {institution.name}
                                                </BaseOption>
                                            )
                                        })}
                                    </BaseSelect>
                                </BaseFieldSet>
                            </Grid2>
                        )}
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={16}
                        lineHeight={"normal"}
                        color={"#FF0000"}
                        whiteSpace={"normal"}
                        marginBlockStart={"var(--basic-margin)"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        component={"div"}
                        className="button-box"
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            radius="7px"
                            fontSize={16}
                            fontWeight={800}
                            bgcolor="#791377"
                            padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Update
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </EditSystemUserWrapper>
        </Layout>
    )
}